<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useSession } from '../auth';
import { trpc } from '../trpc';
import AppHeader from '@/components/layout/AppHeader.vue';
import { Button } from '@/components/ui/button';

interface Deck {
  id: string;
  name: string;
  userId: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

const router = useRouter();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

const decks = ref<Deck[]>([]);
const archivedDecks = ref<Deck[]>([]);
const showArchived = ref(false);
const loading = ref(false);
const error = ref('');

const showCreateModal = ref(false);
const showEditModal = ref(false);
const editingDeck = ref<Deck | null>(null);
const deckName = ref('');
const saving = ref(false);

const openMenuId = ref<string | null>(null);

function toggleMenu(deckId: string) {
  openMenuId.value = openMenuId.value === deckId ? null : deckId;
}

function closeMenu() {
  openMenuId.value = null;
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target.closest('.deck-menu-container')) {
    closeMenu();
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    if (showEditModal.value) {
      closeEditModal();
    } else if (showCreateModal.value) {
      closeCreateModal();
    } else if (openMenuId.value) {
      closeMenu();
    }
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleKeydown);
});

async function fetchDecks() {
  loading.value = true;
  error.value = '';
  try {
    const [activeDecks, archived] = await Promise.all([
      trpc.listDecks.query(),
      trpc.listArchivedDecks.query(),
    ]);
    decks.value = activeDecks;
    archivedDecks.value = archived;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to fetch decks';
  } finally {
    loading.value = false;
  }
}

async function archiveDeck(deck: Deck) {
  try {
    await trpc.archiveDeck.mutate({ id: deck.id });
    await fetchDecks();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to archive deck';
  }
}

async function unarchiveDeck(deck: Deck) {
  try {
    await trpc.unarchiveDeck.mutate({ id: deck.id });
    await fetchDecks();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to unarchive deck';
  }
}

async function deleteDeck(deck: Deck) {
  if (!confirm(`Are you sure you want to delete "${deck.name}"? This will also delete all flashcards in this deck.`)) {
    return;
  }
  try {
    await trpc.deleteDeck.mutate({ id: deck.id });
    await fetchDecks();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to delete deck';
  }
}

function openCreateModal() {
  deckName.value = '';
  showCreateModal.value = true;
}

function closeCreateModal() {
  showCreateModal.value = false;
  deckName.value = '';
}

function openEditModal(deck: Deck) {
  editingDeck.value = deck;
  deckName.value = deck.name;
  showEditModal.value = true;
}

function closeEditModal() {
  showEditModal.value = false;
  editingDeck.value = null;
  deckName.value = '';
}

async function createDeck() {
  if (!deckName.value.trim()) return;
  saving.value = true;
  try {
    await trpc.createDeck.mutate({ name: deckName.value.trim() });
    closeCreateModal();
    await fetchDecks();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to create deck';
  } finally {
    saving.value = false;
  }
}

async function updateDeck() {
  if (!editingDeck.value || !deckName.value.trim()) return;
  saving.value = true;
  try {
    await trpc.updateDeck.mutate({
      id: editingDeck.value.id,
      name: deckName.value.trim(),
    });
    closeEditModal();
    await fetchDecks();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to update deck';
  } finally {
    saving.value = false;
  }
}

function viewDeck(deck: Deck) {
  router.push(`/decks/${deck.id}`);
}

onMounted(() => {
  if (!isLoading.value && !user.value) {
    router.push('/sign-in');
  } else if (user.value) {
    fetchDecks();
  }
});

watch(user, (newUser) => {
  if (newUser) {
    fetchDecks();
  }
});
</script>

<template>
  <div class="min-h-screen bg-background">
    <AppHeader />

    <main class="max-w-4xl mx-auto px-4 py-8">
      <div
        v-if="isLoading"
        class="text-center text-muted-foreground"
      >
        Loading...
      </div>

      <div
        v-else-if="user"
        class="space-y-6"
      >
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-semibold">
            Decks
          </h2>
          <Button @click="openCreateModal">
            New Deck
          </Button>
        </div>

        <div
          v-if="error"
          class="bg-destructive/10 text-destructive p-4 rounded-md"
        >
          {{ error }}
        </div>

        <div
          v-if="loading"
          class="text-center text-muted-foreground"
        >
          Loading decks...
        </div>

        <div
          v-else-if="decks.length === 0"
          class="text-center text-muted-foreground py-8"
        >
          No decks yet. Create your first deck to get started!
        </div>

        <div
          v-else
          class="grid gap-4"
        >
          <div
            v-for="deck in decks"
            :key="deck.id"
            class="bg-card rounded-lg shadow-md p-4 flex items-center gap-2 border border-border cursor-pointer hover:border-primary/50 transition-colors"
            @click="viewDeck(deck)"
          >
            <span class="text-lg font-medium text-foreground flex-1">
              {{ deck.name }}
            </span>
            <span
              v-if="deck.isDefault"
              class="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground"
            >
              Default
            </span>
            <div
              v-if="!deck.isDefault"
              class="relative deck-menu-container"
            >
              <Button
                variant="outline"
                class="px-2"
                size="sm"
                @click.stop="toggleMenu(deck.id)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="1" />
                  <circle cx="12" cy="5" r="1" />
                  <circle cx="12" cy="19" r="1" />
                </svg>
              </Button>
              <div
                v-if="openMenuId === deck.id"
                class="absolute right-0 top-full mt-1 bg-card border border-border rounded-md shadow-lg z-10 min-w-32"
              >
                <button
                  class="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors cursor-pointer"
                  @click.stop="openEditModal(deck); closeMenu()"
                >
                  Edit
                </button>
                <button
                  class="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors cursor-pointer"
                  @click.stop="archiveDeck(deck); closeMenu()"
                >
                  Archive
                </button>
                <button
                  class="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-muted transition-colors cursor-pointer"
                  @click.stop="deleteDeck(deck); closeMenu()"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Archived Decks Section -->
        <div
          v-if="archivedDecks.length > 0"
          class="mt-8"
        >
          <button
            class="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            @click="showArchived = !showArchived"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              :class="{ 'rotate-90': showArchived }"
              class="transition-transform"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
            <span class="text-sm font-medium">
              Archived ({{ archivedDecks.length }})
            </span>
          </button>

          <div
            v-if="showArchived"
            class="mt-4 grid gap-4"
          >
            <div
              v-for="archivedDeck in archivedDecks"
              :key="archivedDeck.id"
              class="bg-card rounded-lg shadow-md p-4 flex items-center gap-2 border border-border opacity-75 cursor-pointer hover:border-primary/50 transition-colors"
              @click="viewDeck(archivedDeck)"
            >
              <span class="text-lg font-medium text-foreground flex-1">
                {{ archivedDeck.name }}
              </span>
              <div class="relative deck-menu-container">
                <Button
                  variant="outline"
                  class="px-2"
                  size="sm"
                  @click.stop="toggleMenu(archivedDeck.id)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle cx="12" cy="12" r="1" />
                    <circle cx="12" cy="5" r="1" />
                    <circle cx="12" cy="19" r="1" />
                  </svg>
                </Button>
                <div
                  v-if="openMenuId === archivedDeck.id"
                  class="absolute right-0 top-full mt-1 bg-card border border-border rounded-md shadow-lg z-10 min-w-32"
                >
                  <button
                    class="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors cursor-pointer"
                    @click.stop="openEditModal(archivedDeck); closeMenu()"
                  >
                    Edit
                  </button>
                  <button
                    class="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors cursor-pointer"
                    @click.stop="unarchiveDeck(archivedDeck); closeMenu()"
                  >
                    Unarchive
                  </button>
                  <button
                    class="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-muted transition-colors cursor-pointer"
                    @click.stop="deleteDeck(archivedDeck); closeMenu()"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="text-center"
      >
        <p class="text-muted-foreground mb-4">
          You need to sign in to access this page.
        </p>
        <Button @click="router.push('/sign-in')">
          Sign In
        </Button>
      </div>
    </main>

    <!-- Create Modal -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
    >
      <div class="bg-card rounded-lg shadow-xl p-6 w-full max-w-md border border-border">
        <h3 class="text-lg font-semibold mb-4">
          Create Deck
        </h3>
        <input
          v-model="deckName"
          type="text"
          placeholder="Deck name"
          class="w-full px-3 py-2 border border-input rounded-md mb-4 bg-background"
          @keyup.enter="createDeck"
        >
        <div class="flex justify-end gap-2">
          <Button
            variant="ghost"
            @click="closeCreateModal"
          >
            Cancel
          </Button>
          <Button
            :disabled="saving || !deckName.trim()"
            @click="createDeck"
          >
            {{ saving ? 'Creating...' : 'Create' }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Edit Deck Modal -->
    <div
      v-if="showEditModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
    >
      <div class="bg-card rounded-lg shadow-xl p-6 w-full max-w-md border border-border">
        <h3 class="text-lg font-semibold mb-4">
          Edit Deck
        </h3>
        <input
          v-model="deckName"
          type="text"
          placeholder="Deck name"
          class="w-full px-3 py-2 border border-input rounded-md mb-4 bg-background"
          @keyup.enter="updateDeck"
        >
        <div class="flex justify-end gap-2">
          <Button
            variant="ghost"
            @click="closeEditModal"
          >
            Cancel
          </Button>
          <Button
            :disabled="saving || !deckName.trim()"
            @click="updateDeck"
          >
            {{ saving ? 'Saving...' : 'Save' }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
